import { EventAvailabilityDetails, EventNoticeDetails } from "features/types";
import React from "react";
import { View, StyleSheet, Image } from "react-native";
import dayjs from "dayjs";
import Chevron from "components/Chevron";
import { Text } from "@ui-kitten/components";
import NoticeTag from "components/NoticeTag";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { EXPERIENCE_EVENT_TYPES, NON_SPORTS_EVENT_TYPES } from "constants/";
import Spacer from "components/Spacer";
import { svgURL } from "utils/fileUtils";
import { eventTypeDisplay } from "utils/eventUtils";
import { hoursToDay } from "utils/dateUtils";

interface ScheduleItemProps {
  event: EventAvailabilityDetails;
  shouldShowEventType: boolean;
  onPress?: () => void;
}

const ScheduleItem: React.FC<ScheduleItemProps> = ({
  event,
  shouldShowEventType,
  onPress,
}) => {
  const {
    localDate,
    timezone,
    containsTime,
    opponentName,
    eventType,
    notices,
    imageUrl,
  } = event;

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const isSportsEvent = !NON_SPORTS_EVENT_TYPES.includes(event.eventType);
  const eventImage = isSportsEvent ? svgURL(opponentName, false) : imageUrl!;

  const Notices = (props: { notices: EventNoticeDetails[] }) => (
    <>
      {props.notices
        .filter((notice) => notice.enabled)
        .map((notice) => (
          <View key={notice.id}>
            {(notice.displayTypes.includes("SCHEDULE_TOOLTIP") ||
              notice.displayTypes.includes("SCHEDULE")) && (
              <NoticeTag
                type={notice.type}
                tag={notice.tag}
                shouldShowPopover={notice.displayTypes.includes(
                  "SCHEDULE_TOOLTIP"
                )}
                description={notice.description}
              ></NoticeTag>
            )}
          </View>
        ))}
    </>
  );

  const EventDate = ({ event }: { event: EventAvailabilityDetails }) => {
    if (event.eventStatus !== "NORMAL") {
      <Text numberOfLines={1} ellipsizeMode="tail" category="h6">
        {event.eventStatus}
      </Text>;
    }
    return (
      <>
        <Text>{dayjs(localDate).tz(timezone).format("ddd")}</Text>
        <Text style={{ fontSize: 18, fontWeight: "bold" }}>
          {dayjs(localDate).tz(timezone).format("MMM DD")}
        </Text>
        {containsTime && (
          <Text numberOfLines={1}>
            {dayjs(localDate).tz(timezone).format("h:mm a")}
          </Text>
        )}
      </>
    );
  };

  const EventImage = () => {
    return (
      <View>
        <Image
          source={{
            uri: eventImage,
          }}
          style={{
            width: isSportsEvent ? 50 : 75,
            height: 50,
            borderRadius: isSportsEvent ? 0 : 8,
          }}
          resizeMode={isSportsEvent ? "contain" : "cover"}
        />
        <>
          {shouldShowEventType && (
            <Text category="c1" style={{ marginTop: 5 }}>
              {eventTypeDisplay(eventType)}
            </Text>
          )}
        </>
      </View>
    );
  };

  if (EXPERIENCE_EVENT_TYPES.includes(event.eventType)) {
    return (
      <View style={styles.container}>
        <View style={{ flex: 0.5 }}>
          <Text style={{ fontSize: 18 }}>{event.eventName}</Text>
          {event.reservable ? (
            <Text style={{ fontSize: 12 }}>{event.venueName}</Text>
          ) : (
            <Text style={{ fontSize: 12 }}>
              Available in {hoursToDay(event.reservableInHours)}
            </Text>
          )}
          <Notices notices={notices} />

          {event.minPlanTicketCharge && !tenantConfig?.hidePremiumTag && (
            <>
              <Spacer margin={2} />
              <NoticeTag type="PREMIUM" onPress={onPress} tag="PREMIUM" />
            </>
          )}
        </View>

        <View style={{ flex: 0.3, alignItems: "center" }}>
          <EventImage />
        </View>
        <View>
          {event.reservable ? (
            <Chevron />
          ) : (
            // works as a placeholder
            <View style={{ width: 36 }}></View>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={{ flex: 0.3 }}>
        <EventDate event={event} />
      </View>
      <View style={{ flex: 0.3 }}>
        <EventImage />
      </View>
      <View style={{ flex: 0.5 }}>
        <Text style={{ fontSize: 18 }}>{event.eventName}</Text>
        {event.reservable ? (
          <Text style={{ fontSize: 12 }}>{event.venueName}</Text>
        ) : (
          <Text style={{ fontSize: 12 }}>
            Available in {hoursToDay(event.reservableInHours)}
          </Text>
        )}
        <Notices notices={notices} />
        {event.minPlanTicketCharge && !tenantConfig?.hidePremiumTag && (
          <>
            <Spacer margin={2} />
            <NoticeTag type="PREMIUM" onPress={onPress} tag="PREMIUM" />
          </>
        )}
      </View>
      <View>
        {event.reservable ? (
          <Chevron />
        ) : (
          // works as a placeholder
          <View style={{ width: 36 }}></View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    marginBottom: 25,
  },
});

export default ScheduleItem;
